/** @jsx jsx */
/** @jsxRuntime classic */

import { Interpolation, jsx } from '@emotion/react';
import React, { useMemo } from 'react';
import { TypographyVariant } from '../style/typography';
import useTheme from '../style/useTheme';
import { ThemeConfig } from '../style/theme';
import { isInternetExplorer } from '../utils';
import { SxProp } from '../style/sxProp';
import { ColorResolveOptions } from '../style/colors';

export type TypographyColor =
  | 'default'
  | 'light'
  | 'title'
  | ColorResolveOptions;

type Props = React.PropsWithChildren<{
  sx?: SxProp;
  weight?: 'normal' | 'bold';
  align?: 'center' | 'left' | 'right';
  className?: string;
  id?: string;
  ['data-testid']?: string;
  variant?: TypographyVariant;
  color?: TypographyColor;
  as?: keyof JSX.IntrinsicElements;
  // Limits the maximum number of lines, and truncates any overflown text
  // with an ellipsis. NOTE: this is NOT supported on IE11.
  maxLines?: number;
  // Forces lines to break/wrap in cases where a long word without breaks (e.g. file
  // name or URL) would otherwise cause the width to exceed beyond its container size.
  forceLineWrap?: boolean;
}>;

const Typography: React.FC<Props> = (props) => {
  const {
    sx,
    weight = 'normal',
    align,
    children,
    color = 'default',
    variant = 'body',
    className,
    id,
    as = 'p',
    'data-testid': dataTestId,
    maxLines,
    forceLineWrap,
  } = props;
  const { theme, resolveTypography, resolveColor, resolveCSS } = useTheme();

  const cssColor = useMemo(() => {
    if (color === 'default' || color === 'light' || color === 'title') {
      return resolveColor({
        type: 'theme',
        colorLegacy: theme.components.text[color].color.legacy,
        colorNext: theme.components.text[color].color.next,
      });
    }
    return resolveColor(color);
  }, [color, resolveColor, theme.components.text]);

  const css: Interpolation<ThemeConfig> = {
    ...resolveTypography(variant),
    margin: 0,
    textAlign: align,
    color: cssColor,
    ...(weight === 'bold' && {
      fontWeight: 700,
    }),
    ...(maxLines && {
      // Limit text to N lines, and adds an ellipsis if there is any
      // overflow: https://css-tricks.com/line-clampin/
      // TODO: implement an IE11 fallback solution
      display: '-webkit-box',
      WebkitLineClamp: maxLines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      // Fallback: add ellipsis when it isn't already added by webkit-line-clamp
      textOverflow: 'ellipsis',
    }),
    ...(forceLineWrap && {
      ...(!isInternetExplorer()
        ? // Attempts to break between words, but breaks in the middle
          // of a long word as a last resort. Not supported on IE11.
          { overflowWrap: 'anywhere' }
        : // IE11 fallback: breaks are added anywhere (without trying
          // to "optimally" break between words when possible).
          // Aside: `overflow-wrap: break-word` works on IE11 and has
          // the same effect, but is a flimsier solution as it requires
          // specific width/min-width settings on parent components, or
          // else the content may overflow and become unusable. Thus,
          // the "safer" solution is used here, at the cost of a
          // slightly worse UX on IE11.
          { wordBreak: 'break-all' }),
    }),
    '@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)':
      {
        maxWidth: '420px',
      },
    ...resolveCSS(sx),
  };

  return jsx(
    as,
    {
      'data-testid': dataTestId,
      className,
      id,
      css,
    },
    children
  );
};

export default Typography;
